/**
 * Membership 5787 — signup flow orchestration.
 *
 * This is one continuous flow (tier -> household -> HH seats -> payment),
 * not four disconnected pages, per the design note in the build brief.
 * getStepsForTier decides which steps apply: First-Year Couples is free,
 * so it skips straight from the seat form to a confirmation — UNLESS
 * additional seats were selected (that tier's included allowance is 0, so
 * any additional seat is a real, member-rate charge — fixed 2026-08-28,
 * see kbt-backend membership.controller.js's free-tier branch), in which
 * case it still needs the payment step to actually collect that charge.
 * buildEnrollPayload assembles everything collected across steps into the
 * single POST /api/membership/enroll body, so the page component only
 * needs to hold state per step and call this once at submit time.
 */
import { seatNamesByGender } from './seatPricing';

export const ALL_STEPS = ['tier', 'household', 'seats', 'payment'];

export const getStepsForTier = (tier, { extraSeatCost = 0 } = {}) => {
  if (tier?.isFree && extraSeatCost <= 0) return ['tier', 'household', 'seats'];
  return ALL_STEPS;
};

// The backend charges exactly what's in `hhSeats` (see kbt-backend
// membership.controller.js) — that's the whole enforcement of "included
// seats are never charged": only additionalSeatPeople ever gets sent
// there. includedSeatPeople still needs to reach the office (who is
// actually sitting in the free seats), so it rides inside `household`,
// which is stored as-is — no backend schema change needed for it.
export const buildEnrollPayload = ({ tier, billingCycle, household, seats }) => ({
  tierId: tier?.id ?? null,
  billingCycle,
  household: {
    ...household,
    includedSeatNames: seatNamesByGender(seats?.includedSeatPeople),
  },
  hhSeats: seatNamesByGender(seats?.additionalSeatPeople),
});

// POST /api/membership/enroll-guest-seats — a guest requesting High
// Holiday seats without becoming a member. No tier/household involved,
// just contact info (so the office and Stripe receipt have somewhere to
// go). A guest has no included allowance, so every seat they enter is in
// additionalSeatPeople and every one is charged.
//
// The seat list goes out as `hhSeats`, the same key buildEnrollPayload
// uses above — kbt-backend reads req.body.hhSeats for BOTH endpoints
// (membership/guestSeatValidation.js, membership/enrollValidation.js).
// This builder sent it as `seats` until 2026-09-07, so the backend saw no
// seat list at all and answered every guest checkout with a 422 "Invalid
// guest seat request". Keep this key identical to buildEnrollPayload's.
export const buildGuestSeatsPayload = ({ guestContact, seats } = {}) => ({
  contact: {
    name: (guestContact?.name || '').trim(),
    email: (guestContact?.email || '').trim(),
  },
  hhSeats: seatNamesByGender(seats?.additionalSeatPeople),
});

// Drives the persistent sticky tally bar's CONTINUE button (wireframe
// section 7's bottom bar, visible through the whole flow, not just the
// final review step). Rather than duplicate each step's own validation,
// this only decides WHERE to scroll — the step's own form still owns
// confirming itself.
export const getNextIncompleteStep = ({ selectedTier, seatsConfirmed, householdConfirmed }) => {
  if (!selectedTier) return 'tier';
  if (!seatsConfirmed) return 'seats';
  if (!householdConfirmed) return 'household';
  return 'review';
};
